import * as THREE from 'three';
import earcut from 'earcut';
import polygonClipping, { Ring as PCRing, MultiPolygon as PCMultiPolygon } from 'polygon-clipping';
import { subtractMeshes, TagRange, TaggedCutter } from './manifoldCsg';
import { arcFromBulge, pointAtArcLength } from './skeleton/arcGeometry';

/**
 * Builder for the no-skeleton 3D floor mesh.
 *
 * Pipeline:
 *  1. 2D shell  = outline outer ring ⊖ outline cavities ⊖ all room polygons
 *  2. Triangulate the shell  → wall-cap triangles tagged '__shell__'
 *     Triangulate each room → room-cap triangles tagged with the roomId
 *  3. Extrude each tagged polygon to its own ceiling height (shell uses the
 *     floor's `ceilingZ`; each room uses its own `room.ceilingZ`). Side quads
 *     inherit the tag of their source polygon.
 *  4. Boolean-subtract opening boxes (doors/windows) using the Manifold WASM
 *     library, which guarantees a watertight two-manifold result AND carries
 *     provenance through the cut: wall faces keep their shell-piece tag and the
 *     reveal faces an opening creates come back tagged with that opening's id.
 *
 * The returned mesh has one geometry with N groups. `groupIdByIndex[i]` is the
 * tag for group i — a shell-piece tag (`__shell__` / `__shell__#n`), a room id
 * (the side walls bounding that room's cutout), or an opening annotation id
 * (the reveal faces an opening cut) — so every triangle traces to its source.
 * Caps + outer walls stay shell; rooms have no floor/ceiling geometry, only the
 * walls around their cutout.
 */

export type Pt2 = { x: number; y: number };

/**
 * Horizontal frame of an opening, derived from its drawn 3D polygon by PCA.
 * Shared by the 3D cutter and the 2D footprint/glyph paths so both agree on
 * the wall axis, width and centre.
 */
export interface OpeningFrame {
  /** Centroid of the polygon's XY positions. */
  center: Pt2;
  /** Unit vector along the wall (the polygon's dominant horizontal axis). */
  wallDir: Pt2;
  /** Unit vector perpendicular to the wall, in plane (90° CCW of wallDir). */
  wallNorm: Pt2;
  /** Half the opening's extent along `wallDir` (so full width = 2·halfWidth). */
  halfWidth: number;
  /**
   * Set when the opening sits on a CURVED wall edge: glyphs bend along it
   * (see skeleton/openingWallMap.ts). `curvature` is the signed 1/radius of
   * the wall CENTERLINE (> 0: the wall turns toward +wallNorm), `perpOffset`
   * how far `center` sits off that centerline along +wallNorm.
   */
  bend?: { curvature: number; perpOffset: number };
}

/**
 * Compute the horizontal {@link OpeningFrame} of an opening from its drawn 3D
 * polygon. PCA on the XY positions gives the dominant horizontal direction (the
 * wall axis); its perpendicular is the wall normal. `halfWidth` is the maximum
 * extent of the polygon along the wall axis from the centroid. Returns null for
 * a degenerate polygon (fewer than 3 points).
 */
export function computeOpeningFrame(polygon3D: Array<{ x: number; y: number }>): OpeningFrame | null {
  if (polygon3D.length < 3) return null;
  let cx = 0;
  let cy = 0;
  for (const p of polygon3D) {
    cx += p.x;
    cy += p.y;
  }
  cx /= polygon3D.length;
  cy /= polygon3D.length;
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const p of polygon3D) {
    const dx = p.x - cx;
    const dy = p.y - cy;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  // Principal axis of the 2x2 covariance [[sxx,sxy],[sxy,syy]].
  const angle = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  const wallDir: Pt2 = { x: Math.cos(angle), y: Math.sin(angle) };
  const wallNorm: Pt2 = { x: -wallDir.y, y: wallDir.x };
  let halfWidth = 0;
  for (const p of polygon3D) {
    const u = (p.x - cx) * wallDir.x + (p.y - cy) * wallDir.y;
    halfWidth = Math.max(halfWidth, Math.abs(u));
  }
  return { center: { x: cx, y: cy }, wallDir, wallNorm, halfWidth };
}

/**
 * Build the horizontal {@link OpeningFrame} for an OPENING_SKETCH parametrically
 * from its wall edge, rather than from a drawn polygon. The opening is a span on
 * the wall edge; `offset`/`length` are measured ALONG the edge (arc length when
 * the edge carries a `bulge`):
 *   wallDir  = local tangent at the span's center (chord direction if straight)
 *   wallNorm = 90° CCW of wallDir
 *   center   = point at arc-length (offset + length/2) + wallNorm·perpOffset
 *   halfWidth = length / 2
 * `perpOffset` shifts the glyph off the wall centerline (signed, +wallNorm side).
 * Returns null for a degenerate (zero-length) edge.
 */
export function frameFromWallOpeningSketch(
  start: Pt2,
  end: Pt2,
  params: { offset: number; length: number; perpOffset: number },
  bulge = 0,
): OpeningFrame | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (Math.hypot(dx, dy) < 1e-9) return null;
  const { point, tangent } = pointAtArcLength(start, end, bulge, params.offset + params.length / 2);
  const wallDir: Pt2 = tangent;
  const wallNorm: Pt2 = { x: -wallDir.y, y: wallDir.x };
  const center: Pt2 = {
    x: point.x + wallNorm.x * params.perpOffset,
    y: point.y + wallNorm.y * params.perpOffset,
  };
  const arc = arcFromBulge(start, end, bulge);
  if (!arc) return { center, wallDir, wallNorm, halfWidth: params.length / 2 };
  // Curved edge: the glyph bends along it — the circle center lies on +wallNorm when the wall turns left.
  const side = Math.sign((arc.center.x - point.x) * wallNorm.x + (arc.center.y - point.y) * wallNorm.y) || 1;
  return { center, wallDir, wallNorm, halfWidth: params.length / 2, bend: { curvature: side / arc.radius, perpOffset: params.perpOffset } };
}

/**
 * Convert an OPENING_SKETCH's stored `inset` (non-negative distance the glyph
 * sits INWARD from the outer wall face on its side) to the signed perpOffset
 * (off the wall centerline, +wallNorm side) that {@link frameFromWallOpeningSketch}
 * consumes.
 *
 * The wall band spans −rightSize..+leftSize about the centerline along +wallNorm
 * (see the PointoramaWallSketch cutout), so:
 *   - Side positive (+wallNorm face at +leftSize):  perpOffset = +leftSize − inset
 *   - Side negative (−wallNorm face at −rightSize): perpOffset = −rightSize + inset
 * With inset = 0 the glyph is flush with the outer face; increasing it moves the
 * glyph toward (and past) the centerline. `swingPositive` picks the face.
 */
export function signedPerpFromInset(inset: number, swingPositive: boolean, leftSize: number, rightSize: number): number {
  const i = Math.max(0, inset);
  return swingPositive ? leftSize - i : -rightSize + i;
}

export interface RoomInput {
  /** Stable identifier used for per-room attribution. */
  id: string;
  /** Closed polygon (no repeated closing vertex). */
  polygon: Pt2[];
  /** Z of the room ceiling. Side walls of this room extrude from `floorZ` to this. */
  ceilingZ: number;
}

export interface OpeningInput {
  /** Stable identifier (opening annotation id) used to attribute the cut faces
   * this opening creates, so result triangles trace back to it. */
  id: string;
  /**
   * Full 3D polygon as drawn by the user (AREA annotation points). Lies
   * approximately on the wall plane — its primary horizontal direction
   * defines the wall axis; the perpendicular horizontal direction is the
   * wall normal (the direction the cutter needs to extrude along to
   * punch through the wall).
   */
  polygon3D: Array<{ x: number; y: number; z: number }>;
  /**
   * Wall thickness hint, in meters. The cutter is extruded by ±thickness/2
   * along the wall normal. Overshoot past the wall faces is NOT generally
   * harmless: near a corner it crosses the junction and carves the adjacent
   * wall, so the hint should hug the real faces (rectangle-fit short axis,
   * or the boundary probe) rather than blanket-overshoot.
   */
  wallThickness: number;
  /**
   * Optional shift of the cutter's center along +wallNorm (meters). Lets a
   * footprint line drawn off the wall's mid-thickness cut an asymmetric
   * depth per side without inflating the symmetric thickness — the
   * corner-safe alternative to overshooting. Sign follows the wallNorm of
   * `computeOpeningFrame(polygon3D)`.
   */
  wallNormShift?: number;
}

export interface FloorMesh3DInput {
  /** Outline outer ring (closed polygon, no repeated closing vertex). */
  outerRing: Pt2[];
  /** Outline cavities (holes in the outer ring). */
  cavities: Pt2[][];
  /** Z of the floor (bottom of every extrusion). */
  floorZ: number;
  /** Z of the shell ceiling (and fallback for rooms without their own height). */
  ceilingZ: number;
  /** Rooms to stamp out and re-cap with their own ceiling. */
  rooms: RoomInput[];
  /** Door / window cuts to apply after extrusion (the legacy OPENING annotations, drawn polygons). */
  openings: OpeningInput[];
  /**
   * Further cutters, built elsewhere and handed over ready-made: the prisms the
   * floor's wall sketch builds for its OPENING_SKETCHes (parametric on its
   * edges, see PointoramaWallSketch.buildOpeningCutters). Tagged like the
   * openings above (`openingTag(id)`), so the reveal faces trace back to the
   * sketch that cut them. Disposed by the build, like every cutter.
   */
  extraCutters?: TaggedCutter[];
}

export interface FloorMesh3DResult {
  geometry: THREE.BufferGeometry;
  /**
   * Parallel to `geometry.groups`: `groupIdByIndex[i]` is the tag for group i
   * — a shell-piece tag (`'__shell__'` / `'__shell__#n'`) for wall faces, or an
   * opening's annotation id for the reveal faces that opening cut. Survives the
   * Manifold boolean (rooms aren't in the geometry yet, so no room tags).
   */
  groupIdByIndex: string[];
  /**
   * Flat list of silhouette edge endpoints for line overlay rendering
   * (mirrors the skeleton's 3D outline pass): pairs of `[x,y,z, x,y,z]`.
   * Includes bottom-cap edges, top-cap edges, and one vertical per polygon
   * vertex. Consumers feed this directly into a LineSegmentsGeometry.
   */
  outlinePositions: number[];
  /**
   * True when the floor had openings but they could NOT be cut (the Manifold
   * WASM module wasn't ready yet), so the geometry is the uncut wall. The
   * caller should rebuild once Manifold is ready. False when there were no
   * openings, or all openings were successfully cut.
   */
  openingsDeferred: boolean;
}

const SHELL_TAG = '__shell__';

// ─── Provenance tags ────────────────────────────────────────────────────
// Each mesh group is tagged so its triangles trace back to a source. Tags are
// self-describing strings carried through the Manifold boolean: the shell uses
// SHELL_TAG (`__shell__` / `__shell__#n` per piece), rooms and openings use a
// `kind:id` form. `parseGroupTag` decodes them for consumers (e.g. export).

/** Tag for a room's cutout walls. */
export const roomTag = (id: string): string => `room:${id}`;
/** Tag for an opening's reveal faces. */
export const openingTag = (id: string): string => `opening:${id}`;

/** Decoded provenance of a mesh group. */
export type GroupProvenance =
  | { kind: 'shell'; id: null }
  | { kind: 'room'; id: string }
  | { kind: 'opening'; id: string };

/** Decode a group tag into its kind + source annotation id. */
export function parseGroupTag(tag: string): GroupProvenance {
  if (tag === SHELL_TAG || tag.startsWith(`${SHELL_TAG}#`)) return { kind: 'shell', id: null };
  if (tag.startsWith('room:')) return { kind: 'room', id: tag.slice('room:'.length) };
  if (tag.startsWith('opening:')) return { kind: 'opening', id: tag.slice('opening:'.length) };
  // Unknown/legacy tag — treat as shell so export still has a bucket for it.
  return { kind: 'shell', id: null };
}

// ─── 2D helpers ─────────────────────────────────────────────────────────

function ringToPC(ring: Pt2[]): PCRing {
  const out: PCRing = ring.map((p) => [p.x, p.y] as [number, number]);
  if (out.length > 0) out.push([out[0][0], out[0][1]]);
  return out;
}

function pcRingToRing(ring: PCRing): Pt2[] {
  // polygon-clipping returns self-closing rings (last == first). Drop the close.
  const out: Pt2[] = [];
  for (let i = 0; i < ring.length - 1; i++) out.push({ x: ring[i][0], y: ring[i][1] });
  return out;
}

/**
 * Outline ⊖ cavities ⊖ rooms → wall-shell polygons (outer + holes per piece).
 *
 * Each hole produced by subtracting a room is matched back to that room (by
 * centroid-in-polygon) so its side walls can be tagged with the room id —
 * polygon-clipping itself doesn't carry which room a hole came from. Holes that
 * are pre-existing cavities (not a room) get a null tag and stay shell.
 */
function computeShellPolygons(input: FloorMesh3DInput): Array<{ outer: Pt2[]; holes: Pt2[][]; holeTags: (string | null)[] }> {
  const { outerRing, cavities, rooms } = input;
  if (outerRing.length < 3) return [];

  const footprint: PCRing[] = [ringToPC(outerRing), ...cavities.filter((c) => c.length >= 3).map(ringToPC)];
  const validRooms = rooms.filter((r) => r.polygon.length >= 3);
  const roomPolys: PCRing[][] = validRooms.map((r) => [ringToPC(r.polygon)]);

  const result: PCMultiPolygon = roomPolys.length > 0 ? polygonClipping.difference(footprint, ...roomPolys) : [footprint];

  const out: Array<{ outer: Pt2[]; holes: Pt2[][]; holeTags: (string | null)[] }> = [];
  for (const poly of result) {
    if (poly.length === 0) continue;
    const outer = pcRingToRing(poly[0]);
    if (outer.length < 3) continue;
    const holes: Pt2[][] = [];
    const holeTags: (string | null)[] = [];
    for (const ring of poly.slice(1)) {
      const hole = pcRingToRing(ring);
      if (hole.length < 3) continue;
      holes.push(hole);
      // A hole is a room cutout if its centroid lies inside a room polygon.
      const c = ringCentroid(hole);
      const room = validRooms.find((r) => pointInPolygon(c, r.polygon));
      holeTags.push(room ? roomTag(room.id) : null);
    }
    out.push({ outer, holes, holeTags });
  }
  return out;
}

/** Area-weighted centroid of a polygon ring. */
function ringCentroid(ring: Pt2[]): Pt2 {
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const cross = a.x * b.y - b.x * a.y;
    area += cross;
    cx += (a.x + b.x) * cross;
    cy += (a.y + b.y) * cross;
  }
  if (Math.abs(area) < 1e-12) {
    // Degenerate — fall back to the vertex average.
    const avg = ring.reduce((s, p) => ({ x: s.x + p.x, y: s.y + p.y }), { x: 0, y: 0 });
    return { x: avg.x / ring.length, y: avg.y / ring.length };
  }
  return { x: cx / (3 * area), y: cy / (3 * area) };
}

/** Standard ray-casting point-in-polygon test. */
function pointInPolygon(p: Pt2, ring: Pt2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const intersects = a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

// ─── Triangulation + extrusion of a single tagged polygon ──────────────

export interface TagSegment {
  tag: string;
  start: number;
  count: number;
}

export interface ExtrusionAccumulator {
  positions: number[];
  indices: number[];
  /**
   * Index-buffer segments accumulated so far, in emission order. A tag may
   * appear in MORE than one segment (e.g. a room id reused for several wall
   * stretches, or across shell pieces) — runs aren't required to be unique or
   * contiguous per tag.
   */
  tagSegments: TagSegment[];
  /** Currently-open segment. The accumulator writes indices into it. */
  currentSegment: TagSegment | null;
  /** Cap edges that count as "wall edges" for the heatmap shader. */
  wallEdges: Array<{ start: Pt2; end: Pt2 }>;
  /**
   * Silhouette outline pairs `[x,y,z, x,y,z, ...]` for the line overlay.
   * Mirrors the skeleton's 3D outline pass: every polygon edge contributes a
   * bottom segment + a top segment, and every polygon vertex a vertical.
   */
  outlinePositions: number[];
}

export function newAccumulator(): ExtrusionAccumulator {
  return { positions: [], indices: [], tagSegments: [], currentSegment: null, wallEdges: [], outlinePositions: [] };
}

/**
 * Coalesce adjacent same-tag index segments into {@link TagRange}s for the
 * Manifold boolean's provenance + the uncut group layout. Non-adjacent same-tag
 * segments stay separate runs.
 */
export function coalesceTagSegments(segments: TagSegment[]): TagRange[] {
  const ranges: TagRange[] = [];
  for (const seg of segments) {
    const prev = ranges[ranges.length - 1];
    if (prev && prev.tag === seg.tag && prev.start + prev.count === seg.start) {
      prev.count += seg.count;
    } else {
      ranges.push({ start: seg.start, count: seg.count, tag: seg.tag });
    }
  }
  return ranges;
}

function beginTag(acc: ExtrusionAccumulator, tag: string): void {
  // Close any open segment first, then open a fresh one. Tags may repeat across
  // segments; each open/close produces one contiguous run.
  endTag(acc);
  acc.currentSegment = { tag, start: acc.indices.length, count: 0 };
}

function endTag(acc: ExtrusionAccumulator): void {
  const seg = acc.currentSegment;
  if (!seg) return;
  seg.count = acc.indices.length - seg.start;
  if (seg.count > 0) acc.tagSegments.push(seg);
  acc.currentSegment = null;
}

function pushVertex(acc: ExtrusionAccumulator, x: number, y: number, z: number): number {
  const idx = acc.positions.length / 3;
  acc.positions.push(x, y, z);
  return idx;
}

function pushTriangle(acc: ExtrusionAccumulator, a: number, b: number, c: number): void {
  acc.indices.push(a, b, c);
}

/**
 * Triangulate one polygon-with-holes at z = `floorZ`, mirror it at z = `topZ`,
 * stitch side quads between corresponding edges, and tag everything under
 * `tag` — EXCEPT each hole ring's side walls, which take `holeTags[i]` when
 * non-null (a room id), so the walls bounding a room cutout trace to that room.
 * Caps and the outer-ring walls always stay `tag` (the shell).
 *
 * `emitOutline` controls whether this polygon contributes silhouette segments
 * for the line overlay. Pass `false` for room prisms when they're not being
 * rendered so the outline doesn't show "ghost" room boundaries floating in
 * the shell.
 */
export function extrudeTaggedPolygon(
  acc: ExtrusionAccumulator,
  outer: Pt2[],
  holes: Pt2[][],
  floorZ: number,
  topZ: number,
  tag: string,
  emitOutline: boolean,
  holeTags: (string | null)[] = [],
): void {
  if (outer.length < 3) return;

  beginTag(acc, tag);

  // earcut wants a flat coords array + holeIndices (vertex offsets where each hole starts)
  const flat: number[] = [];
  for (const p of outer) flat.push(p.x, p.y);
  const holeIndices: number[] = [];
  for (const h of holes) {
    holeIndices.push(flat.length / 2);
    for (const p of h) flat.push(p.x, p.y);
  }
  const triIndices = earcut(flat, holeIndices.length > 0 ? holeIndices : null, 2);

  const totalVerts = flat.length / 2;
  // Bottom and top vertex bases. Bottom vertices are first; top come right after.
  const bottomBase = acc.positions.length / 3;
  for (let i = 0; i < totalVerts; i++) pushVertex(acc, flat[i * 2], flat[i * 2 + 1], floorZ);
  const topBase = acc.positions.length / 3;
  for (let i = 0; i < totalVerts; i++) pushVertex(acc, flat[i * 2], flat[i * 2 + 1], topZ);

  // Bottom cap (CW from above = correct winding for downward face)
  for (let i = 0; i < triIndices.length; i += 3) {
    pushTriangle(acc, bottomBase + triIndices[i], bottomBase + triIndices[i + 2], bottomBase + triIndices[i + 1]);
  }
  // Top cap (CCW from above)
  for (let i = 0; i < triIndices.length; i += 3) {
    pushTriangle(acc, topBase + triIndices[i], topBase + triIndices[i + 1], topBase + triIndices[i + 2]);
  }

  // Side walls — walk every ring, emit two triangles per edge.
  // Ring vertex layout in `flat`: [outer..., hole0..., hole1..., ...].
  // Ring 0 is the outer boundary (stays `tag`); each later ring is a hole whose
  // walls take its room tag (holeTags[r-1]) when set.
  const ringStarts: number[] = [0, ...holeIndices, totalVerts];
  for (let r = 0; r < ringStarts.length - 1; r++) {
    // Switch the open tag for this ring's walls: outer ring → base tag, hole
    // ring → its room tag (or base tag when the hole isn't a room).
    const ringTag = r === 0 ? tag : holeTags[r - 1] ?? tag;
    beginTag(acc, ringTag);

    const start = ringStarts[r];
    const end = ringStarts[r + 1];
    const len = end - start;
    for (let i = 0; i < len; i++) {
      const a = start + i;
      const b = start + ((i + 1) % len);
      // Side quad (bottom-a, bottom-b, top-b, top-a) — orientation chosen so
      // outward normals point away from the polygon's filled side.
      pushTriangle(acc, bottomBase + a, bottomBase + b, topBase + b);
      pushTriangle(acc, bottomBase + a, topBase + b, topBase + a);

      const ax = flat[a * 2];
      const ay = flat[a * 2 + 1];
      const bx = flat[b * 2];
      const by = flat[b * 2 + 1];
      // Record the edge in 2D for heatmap consumers.
      acc.wallEdges.push({ start: { x: ax, y: ay }, end: { x: bx, y: by } });
      if (emitOutline) {
        // Bottom + top cap segments and the vertical at vertex `a` for the
        // line overlay. Vertical at vertex `b` will be emitted when the next
        // iteration treats `b` as its `a`.
        acc.outlinePositions.push(ax, ay, floorZ, bx, by, floorZ);
        acc.outlinePositions.push(ax, ay, topZ, bx, by, topZ);
        acc.outlinePositions.push(ax, ay, floorZ, ax, ay, topZ);
      }
    }
  }

  endTag(acc);
}

// ─── Opening cut boxes ──────────────────────────────────────────────────

/**
 * Build a cutter geometry for a single opening, sized to fully punch through
 * the wall. The footprint quad spans wall thickness (computed by
 * `computeOpeningRectangle`), but we inflate every edge outward along its
 * outward normal so the cutter strictly straddles both wall surfaces. CSG
 * SUBTRACTION on perfectly-coincident or barely-overlapping surfaces produces
 * degenerate output (the hole reads as a thin slab carved into the wall
 * surface instead of a clean through-cut).
 */
export const OPENING_INFLATE = 0.05; // 5 cm overshoot past every wall face

/**
 * Project a 3D polygon onto a best-fit vertical plane and extrude it across
 * the wall normal into a closed cutter mesh.
 *
 * Pipeline:
 *  1. PCA on the polygon's XY positions to find the dominant horizontal
 *     direction (the wall axis). Its perpendicular is the wall normal.
 *  2. Build a 2D cross-section in (alongWall, z) coordinates. The cross-
 *     section *follows the user-drawn polygon shape*, so a triangular or
 *     curved opening produces a triangular or curved cut.
 *  3. Sweep that cross-section ±(wallThickness/2 + OPENING_INFLATE) along
 *     the wall normal direction. The result is a prism whose front and
 *     back faces are the cross-section, with quad sides connecting them.
 *  4. CAP extension: if the cross-section reaches the floor cap (sill near
 *     floorZ) or the ceiling cap (head near ceilingZ), extend the
 *     cross-section past the cap so CSG pierces it cleanly.
 */
export function buildOpeningCutter(opening: OpeningInput, floorZ: number, ceilingZ: number): THREE.BufferGeometry | null {
  const poly = opening.polygon3D;
  if (poly.length < 3) return null;

  // ─── 1. PCA wall direction ─────────────────────────────────────────
  // Compute the dominant horizontal direction from the polygon's XY
  // covariance. For a door/window the long axis is along the wall.
  const frame = computeOpeningFrame(poly);
  if (!frame) return null;
  const { center, wallDir, wallNorm } = frame;
  const cx = center.x;
  const cy = center.y;
  const wallDirX = wallDir.x;
  const wallDirY = wallDir.y;
  const wallNormX = wallNorm.x;
  const wallNormY = wallNorm.y;

  // ─── 2. Project polygon → (alongWall, z) cross-section ─────────────
  type CrossPt = { u: number; z: number };
  const cross: CrossPt[] = poly.map((p) => ({
    u: (p.x - cx) * wallDirX + (p.y - cy) * wallDirY,
    z: p.z,
  }));

  // ─── Cap extension ────────────────────────────────────────────────
  // If the cross-section's minimum z is within OPENING_INFLATE*2 of floorZ,
  // snap it down past the floor so the cap gets pierced. Same logic for
  // the ceiling.
  const CAP_THRESHOLD = OPENING_INFLATE * 2;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const c of cross) {
    if (c.z < minZ) minZ = c.z;
    if (c.z > maxZ) maxZ = c.z;
  }
  if (!(maxZ > minZ)) return null;
  const extendBottom = minZ - floorZ <= CAP_THRESHOLD;
  const extendTop = ceilingZ - maxZ <= CAP_THRESHOLD;
  if (extendBottom || extendTop) {
    for (const c of cross) {
      if (extendBottom && Math.abs(c.z - minZ) < CAP_THRESHOLD) c.z = floorZ - OPENING_INFLATE;
      if (extendTop && Math.abs(c.z - maxZ) < CAP_THRESHOLD) c.z = ceilingZ + OPENING_INFLATE;
    }
  }

  // ─── 3. Normalize cross-section to CCW ────────────────────────────
  // Cutter winding must be consistent: cross-section CCW in (u, z), with
  // the prism swept in +wallNorm direction. We test by signed area in
  // (u, z) and reverse if CW.
  let area = 0;
  for (let i = 0; i < cross.length; i++) {
    const a = cross[i];
    const b = cross[(i + 1) % cross.length];
    area += a.u * b.z - b.u * a.z;
  }
  if (area < 0) cross.reverse();

  // ─── 4. Triangulate cross-section ─────────────────────────────────
  const flat: number[] = [];
  for (const c of cross) flat.push(c.u, c.z);
  const tris = earcut(flat, null, 2);
  if (tris.length === 0) return null;

  // ─── 5. Sweep into prism ──────────────────────────────────────────
  // Half-thickness offset along the wall normal. Includes OPENING_INFLATE
  // overshoot on each side so the cutter strictly straddles both wall
  // faces (CSG SUBTRACTION is unstable on coplanar surfaces).
  const halfThick = opening.wallThickness / 2 + OPENING_INFLATE;
  const normShift = opening.wallNormShift ?? 0;
  const positions: number[] = [];
  const indices: number[] = [];
  const n = cross.length;

  // Front face vertices (cross-section pushed +wallNorm)
  for (const c of cross) {
    positions.push(cx + wallDirX * c.u + wallNormX * (normShift + halfThick), cy + wallDirY * c.u + wallNormY * (normShift + halfThick), c.z);
  }
  // Back face vertices (cross-section pushed −wallNorm)
  for (const c of cross) {
    positions.push(cx + wallDirX * c.u + wallNormX * (normShift - halfThick), cy + wallDirY * c.u + wallNormY * (normShift - halfThick), c.z);
  }

  // Winding (right-handed in world XYZ):
  //   Front face vertices sit on +wallNorm side, back on −wallNorm side.
  //   wallNorm = (−wallDirY, wallDirX, 0), so the basis (wallDir, ẑ,
  //   wallNorm) is LEFT-handed. Therefore a polygon CCW in (u, z) appears
  //   CW when viewed from +wallNorm — earcut's CCW output triangles need
  //   to be reversed to face +wallNorm outward, and used as-is to face
  //   −wallNorm outward.
  //   Sides walk the polygon CCW in (u, z). The side rectangle's
  //   outward 3D normal must point away from the prism interior. With
  //   left-handed (u, z, +wallNorm), the outward direction for edge
  //   (a → b) is obtained by triangle (front-a, front-b, back-b) and
  //   (front-a, back-b, back-a).
  // Front cap (outward = +wallNorm)
  for (let i = 0; i < tris.length; i += 3) indices.push(tris[i], tris[i + 2], tris[i + 1]);
  // Back cap (outward = -wallNorm)
  for (let i = 0; i < tris.length; i += 3) indices.push(n + tris[i], n + tris[i + 1], n + tris[i + 2]);
  // Sides
  for (let i = 0; i < n; i++) {
    const a = i;
    const b = (i + 1) % n;
    indices.push(a, b, n + b);
    indices.push(a, n + b, n + a);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setIndex(indices);
  return geom;
}

/**
 * Vertical prism cutter from an arbitrary 2D footprint ring — the CURVED-wall
 * counterpart of {@link buildOpeningCutter}'s straight box. The footprint is
 * the opening span's tessellated arc band (with overshoot past both wall
 * faces baked in by the caller); extrusion is straight up from `zBottom` to
 * `zTop` (pass them pre-extended past any floor/ceiling caps). Returns a
 * watertight indexed geometry, or null for a degenerate ring.
 */
export function buildVerticalPrismCutter(ring: Pt2[], zBottom: number, zTop: number): THREE.BufferGeometry | null {
  if (ring.length < 3 || !(zTop > zBottom)) return null;
  // Normalize to CCW in XY so cap/side windings below are consistent.
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    area += a.x * b.y - b.x * a.y;
  }
  const pts = area < 0 ? ring.slice().reverse() : ring;
  const flat: number[] = [];
  for (const p of pts) flat.push(p.x, p.y);
  const tris = earcut(flat, null, 2);
  if (tris.length === 0) return null;

  const n = pts.length;
  const positions: number[] = [];
  for (const p of pts) positions.push(p.x, p.y, zBottom);
  for (const p of pts) positions.push(p.x, p.y, zTop);

  const indices: number[] = [];
  // Bottom cap (outward −z): reverse earcut's CCW-in-XY winding.
  for (let i = 0; i < tris.length; i += 3) indices.push(tris[i], tris[i + 2], tris[i + 1]);
  // Top cap (outward +z).
  for (let i = 0; i < tris.length; i += 3) indices.push(n + tris[i], n + tris[i + 1], n + tris[i + 2]);
  // Sides — for a CCW ring the outward normal of edge a→b is its right perp.
  for (let i = 0; i < n; i++) {
    const a = i;
    const b = (i + 1) % n;
    indices.push(a, b, n + b);
    indices.push(a, n + b, n + a);
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setIndex(indices);
  return geom;
}

// ─── Entry point ────────────────────────────────────────────────────────

export function buildFloorMesh3D(input: FloorMesh3DInput): FloorMesh3DResult & { wallEdges: Array<{ start: Pt2; end: Pt2 }> } {
  // outlinePositions is part of FloorMesh3DResult; wallEdges stays separate
  // until the heatmap shader path exists to consume it.
  const acc = newAccumulator();

  // 1+2: shell + per-room extrusions
  const shellPolys = computeShellPolygons(input);
  for (let i = 0; i < shellPolys.length; i++) {
    const piece = shellPolys[i];
    // Each disconnected shell piece becomes its own tagged sub-mesh. We append
    // the piece index so a multi-piece shell still has one tag per sub-mesh
    // (tags must be unique within a build).
    const tag = shellPolys.length === 1 ? SHELL_TAG : `${SHELL_TAG}#${i}`;
    extrudeTaggedPolygon(acc, piece.outer, piece.holes, input.floorZ, input.ceilingZ, tag, true, piece.holeTags);
  }

  // Per-room volumes are intentionally NOT extruded here. They survive in
  // the shell's holes (the 2D difference) and that's all we need to render.
  // When/if per-wall-by-room attribution becomes useful, do it by walking
  // each shell side wall at build time and looking up which room polygon
  // (if any) it borders — not by stamping the room volumes into the
  // geometry, since CSG won't preserve that tagging cleanly across cuts.

  // Build the pre-CSG geometry with groups.
  const preGeom = new THREE.BufferGeometry();
  preGeom.setAttribute('position', new THREE.Float32BufferAttribute(acc.positions, 3));
  preGeom.setIndex(acc.indices);

  // Tag ranges drive both the uncut group layout and the provenance carried
  // through the Manifold boolean. Coalesce adjacent same-tag segments to keep
  // the group list compact; non-adjacent same-tag segments stay separate runs.
  const tagRanges: TagRange[] = coalesceTagSegments(acc.tagSegments);
  const applyGroups = (geom: THREE.BufferGeometry, ranges: TagRange[]): string[] => {
    const ids: string[] = [];
    ranges.forEach((r, i) => {
      geom.addGroup(r.start, r.count, i);
      ids.push(r.tag);
    });
    return ids;
  };

  // 3: opening cuts (only if there are any). The Manifold boolean produces a
  // watertight, two-manifold, *indexed* result — unlike three-bvh-csg, whose
  // subtraction left loose/stray triangles and T-junctions along every cut.
  let outGeom = preGeom;
  let groupIdByIndex: string[];
  let openingsDeferred = false;
  const cutters: TaggedCutter[] = [
    ...input.openings.map((o) => ({ geometry: buildOpeningCutter(o, input.floorZ, input.ceilingZ), tag: openingTag(o.id) })).filter((c): c is TaggedCutter => c.geometry !== null),
    ...(input.extraCutters ?? []),
  ];

  if (cutters.length > 0) {
    // Pass tag ranges through so wall faces keep their shell tag and the reveal
    // faces come back tagged with the opening that cut them. Manifold rebuilds
    // the groups + tag map on the result.
    const cut = subtractMeshes(preGeom, tagRanges, cutters);
    cutters.forEach((c) => c.geometry.dispose());

    if (cut) {
      preGeom.dispose();
      outGeom = cut.geometry; // already has normals + groups from the wrapper
      groupIdByIndex = cut.groupTags;
    } else {
      // Manifold not ready (WASM still loading on initial page load) or the
      // boolean failed. Fall back to the uncut wall — the floor still renders,
      // and `openingsDeferred` tells the caller to rebuild once Manifold is
      // ready. See manifoldCsg.ts / PointoramaFloorMesh3D's onManifoldReady.
      openingsDeferred = true;
      preGeom.computeVertexNormals();
      groupIdByIndex = applyGroups(preGeom, tagRanges);
    }
  } else {
    // No cuts — preGeom still needs normals for the MeshBasicMaterial.
    preGeom.computeVertexNormals();
    groupIdByIndex = applyGroups(preGeom, tagRanges);
  }

  return { geometry: outGeom, groupIdByIndex, wallEdges: acc.wallEdges, outlinePositions: acc.outlinePositions, openingsDeferred };
}
